import { motion } from 'framer-motion';
import { Sunrise, Sun, Sunset, Moon, Clock } from 'lucide-react';
import { PHASES, srcForPhase } from './timeOfDay';

const ICONS = { dawn: Sunrise, day: Sun, sunset: Sunset, night: Moon };

// Light size scales with the viewport so it stays proportional to the photo.
const DOT = 'absolute -translate-x-1/2 -translate-y-1/2 rounded-full';

/** Blinking lights drawn over the photo at measured spots (md+ only: on mobile the photo is cropped). */
function LightFx({ fx, reducedMotion }) {
  const loop = (duration, extra = {}) =>
    reducedMotion ? { duration: 0 } : { duration, repeat: Infinity, ease: 'linear', ...extra };
  const at = ([x, y]) => ({ left: `${x}%`, top: `${y}%` });
  return (
    <div className="pointer-events-none absolute inset-0 hidden md:block">
      {fx.wingtip && (
        <>
          {/* Steady red nav light, slow breathing. */}
          <motion.span
            className={`${DOT} h-[0.7vw] w-[0.7vw] bg-red-500/70`}
            style={{ ...at(fx.wingtip), boxShadow: '0 0 1.2vw 0.4vw rgba(239,68,68,0.55)' }}
            animate={reducedMotion ? undefined : { opacity: [0.55, 1, 0.55] }}
            transition={loop(3.2, { ease: 'easeInOut' })}
          />
          {/* White strobe: double flash every 1.6 s. */}
          <motion.span
            className={`${DOT} h-[0.55vw] w-[0.55vw] bg-white`}
            style={{ ...at(fx.wingtip), boxShadow: '0 0 1.6vw 0.7vw rgba(255,255,255,0.9)' }}
            initial={{ opacity: 0 }}
            animate={reducedMotion ? undefined : { opacity: [0, 1, 0, 1, 0, 0] }}
            transition={loop(1.6, { times: [0, 0.04, 0.1, 0.16, 0.22, 1] })}
          />
        </>
      )}
      {fx.tug && (
        /* Amber rotating beacon on the TLD tug. */
        <motion.span
          className={`${DOT} h-[0.5vw] w-[0.5vw] bg-amber-400`}
          style={{ ...at(fx.tug), boxShadow: '0 0 1.2vw 0.45vw rgba(251,191,36,0.8)' }}
          animate={reducedMotion ? undefined : { opacity: [0.15, 1, 0.15] }}
          transition={loop(1)}
        />
      )}
    </div>
  );
}

/**
 * Full-bleed hero photo; every phase is stacked and the active one fades in.
 * `weights` (Auto mode) gives each phase its own opacity so photos cross-fade
 * slowly before a boundary; without it the active phase fades in over 1.6 s.
 */
export function TimeOfDayHero({ phase, weights = null, reducedMotion = false }) {
  return (
    <div className="fixed inset-0 z-0 overflow-hidden bg-[#0b1220]" aria-hidden="true">
      {PHASES.map((p) => {
        const opacity = weights ? weights[p.id] : p.id === phase ? 1 : 0;
        const fade = weights ? 'opacity 60s linear' : 'opacity 1600ms cubic-bezier(0.4, 0, 0.2, 1)';
        return (
          <div
            key={p.id}
            className="absolute inset-x-0 top-[-4%] h-full md:top-0 md:h-auto md:pt-[3%]"
            style={{
              backgroundColor: p.sky,
              // Raised so the glass cards sit on the tarmac, not on the fuselage.
              WebkitMaskImage: 'linear-gradient(to bottom, #000 80%, transparent 100%)',
              maskImage: 'linear-gradient(to bottom, #000 80%, transparent 100%)',
              opacity,
              transition: reducedMotion ? 'none' : fade,
            }}
          >
            <div className="relative h-full w-full md:aspect-video md:h-auto">
              <img
                src={srcForPhase(p)}
                alt=""
                decoding="async"
                draggable={false}
                className="h-full w-full select-none object-cover"
                style={{ objectPosition: '66% 62%' }}
              />
              {p.fx && opacity > 0 && <LightFx fx={p.fx} reducedMotion={reducedMotion} />}
            </div>
          </div>
        );
      })}
      {/* Keeps white headline and glass cards legible in every lighting. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg, rgba(5,10,22,0.62) 0%, rgba(5,10,22,0.18) 34%, rgba(5,10,22,0) 52%, rgba(5,10,22,0.55) 100%)',
        }}
      />
    </div>
  );
}

/** Apple-style segmented control: Auto (clock) plus the four lighting phases. */
export function TimeOfDayControl({ mode, setMode }) {
  const options = [{ id: 'auto', label: 'Auto', Icon: Clock }, ...PHASES.map((p) => ({ id: p.id, label: p.label, Icon: ICONS[p.id] }))];
  return (
    <div
      role="group"
      aria-label="Lighting"
      className="inline-flex items-center gap-0.5 rounded-full border border-white/20 p-1 backdrop-blur-xl"
      style={{ background: 'rgba(255,255,255,0.10)' }}
    >
      {options.map(({ id, label, Icon }) => {
        const on = mode === id;
        return (
          <button
            key={id}
            type="button"
            aria-pressed={on}
            aria-label={label}
            title={label}
            onClick={() => setMode(id)}
            className={`flex h-8 items-center gap-1.5 rounded-full px-2.5 text-xs font-display font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/70 ${
              on ? 'bg-white/90 text-slate-900' : 'text-white/80 hover:bg-white/15 hover:text-white'
            }`}
          >
            <Icon size={14} strokeWidth={1.75} />
            <span className="hidden sm:inline">{label}</span>
          </button>
        );
      })}
    </div>
  );
}
